/**
 * Persistence for the user's chosen background effect (SC-158, AC6).
 *
 * Two layers, deliberately:
 *  - **`localStorage`** — synchronous, so the effect is known before any async
 *    config resolves. This is what lets the camera publish already carrying the
 *    effect instead of flashing an unprocessed frame first. One copy per Parse
 *    user (SC-452), so the next person on a shared browser never starts with
 *    the last one's background. With no Parse user (signed out, a meeting
 *    guest) nothing is kept on the device: a pick lasts for that page session.
 *  - **`PlayerConfig`** — durable and cross-device, but async. It wins once
 *    loaded, and every write updates both.
 *
 * Everything read back from either layer goes through `parseBackgroundEffect`.
 * `localStorage` is user-editable and may hold a value written by an older
 * build, so stored data is treated as untrusted input rather than as a
 * `BackgroundEffect` we can hand straight to the processor.
 */
import Parse from 'parse'

import { playerConfigService } from './back4app/PlayerConfigService'
import { parseUserId } from '../utils/parseUserId'
import { reportEndedSession } from './endedSessionHandover'
import { NO_BACKGROUND_EFFECT, type BackgroundEffect } from './liveKitVideoBackground'

const BACKGROUND_EFFECT_KEY_PREFIX = 'preferredBackgroundEffect:'
/** SC-452: the one copy every user of the browser shared, before it was per user. */
const LEGACY_BACKGROUND_EFFECT_KEY = 'preferredBackgroundEffect'
/**
 * SC-452: the choice made with no Parse user, a meeting guest's. In memory, so
 * it is gone on reload, yet a menu or dialog that mounts later in the call
 * still shows the effect the camera has.
 */
let sessionOnlyEffect: BackgroundEffect = NO_BACKGROUND_EFFECT

const BLUR_INTENSITIES = new Set(['light', 'strong'])

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0
}

/**
 * Validate an unknown value into a `BackgroundEffect`, or `null` if it is not
 * one. Unknown kinds return `null` rather than throwing so a config written by a
 * newer build simply falls back to no effect on an older one.
 */
export function parseBackgroundEffect(raw: unknown): BackgroundEffect | null {
  if (!raw || typeof raw !== 'object') return null
  const candidate = raw as Record<string, unknown>

  switch (candidate.kind) {
    case 'none':
      return { kind: 'none' }
    case 'blur':
      return typeof candidate.intensity === 'string' && BLUR_INTENSITIES.has(candidate.intensity)
        ? { kind: 'blur', intensity: candidate.intensity as 'light' | 'strong' }
        : null
    case 'preset':
      return isNonEmptyString(candidate.id) ? { kind: 'preset', id: candidate.id } : null
    case 'workspace':
      return isNonEmptyString(candidate.backgroundId)
        ? { kind: 'workspace', backgroundId: candidate.backgroundId }
        : null
    case 'custom':
      return isNonEmptyString(candidate.backgroundId)
        ? { kind: 'custom', backgroundId: candidate.backgroundId }
        : null
    default:
      return null
  }
}

/**
 * The instantly-available preference of `userId`, by default whoever Parse has
 * signed in; with no user, this page session's choice (`none` until one is
 * made). Returns `none` for anything unreadable — absent, malformed JSON, or a
 * shape we do not recognise — so a corrupt value costs the user their effect,
 * never their call.
 */
export function readCachedBackgroundEffect(
  userId: string | null = currentUserId()
): BackgroundEffect {
  try {
    // SC-452: the shared copy is whoever picked last on this browser, not
    // necessarily this user. Nobody reads it; the first read drops it.
    localStorage.removeItem(LEGACY_BACKGROUND_EFFECT_KEY)
  } catch {
    // Blocked storage: there is no copy to drop, nor any to read below.
  }
  if (!userId) return sessionOnlyEffect
  try {
    const stored = localStorage.getItem(`${BACKGROUND_EFFECT_KEY_PREFIX}${userId}`)
    if (!stored) return NO_BACKGROUND_EFFECT
    return parseBackgroundEffect(JSON.parse(stored)) ?? NO_BACKGROUND_EFFECT
  } catch {
    // Malformed JSON, or storage blocked entirely (private mode, disabled
    // cookies). Neither is worth surfacing — the feature just starts off.
    return NO_BACKGROUND_EFFECT
  }
}

/** Remember `effect` on this device for `userId`; with no user, for this page session only. */
export function writeCachedBackgroundEffect(
  effect: BackgroundEffect,
  userId: string | null = currentUserId()
): void {
  if (!userId) {
    sessionOnlyEffect = effect
    return
  }
  try {
    localStorage.setItem(`${BACKGROUND_EFFECT_KEY_PREFIX}${userId}`, JSON.stringify(effect))
  } catch {
    // Storage full or blocked. The effect still applies for this session; only
    // the memory of it is lost, which is not worth interrupting the user for.
  }
}

/** Whether a Parse session exists yet. See `loadDurableBackgroundEffect`. */
function isAuthenticated(): boolean {
  return currentUserId() !== null
}

function currentUserId(): string | null {
  return parseUserId(Parse)
}

/**
 * The signed-in Parse user's id, or null. This, not the Redux store, owns what
 * is read and written here: Parse has a user before the store does and drops
 * them before it does.
 */
export { currentUserId as currentParseUserId }

/**
 * The durable, cross-device preference, with the Parse user whose session the
 * lookup ran under. Returns `null` when there is nothing stored or the lookup
 * fails, letting the caller keep the cached value rather than resetting a
 * working effect because the network hiccuped.
 *
 * Reads the user's own Profile row — the one `saveBackgroundEffectPreference`
 * writes, found by `userId` like every other Profile lookup — not a row found
 * by the `user` pointer, which cloud-created Profiles do not have. Reading that
 * one returned defaults on every device, so a choice made on one never reached
 * another.
 *
 * **Waits for a Parse session first.** Without one there is no user to look
 * up, and an earlier reader answered that with an empty result, losing the
 * cross-device preference silently. Polling briefly for the session avoids
 * that.
 */
export async function loadDurableBackgroundEffect(
  options: { timeoutMs?: number; pollMs?: number } = {}
): Promise<{ effect: BackgroundEffect; userId: string } | null> {
  return (await loadDurableBackgroundEffectOutcome(options)).read
}

/**
 * Same lookup, but says whether it failed. A `null` read alone cannot tell "nothing saved" from
 * "could not read" (no session in time, network, dead session), and a caller that reuses an
 * answer must not reuse a failed one.
 */
export async function loadDurableBackgroundEffectOutcome(
  { timeoutMs = 10000, pollMs = 250 }: { timeoutMs?: number; pollMs?: number } = {}
): Promise<{ failed: boolean; read: { effect: BackgroundEffect; userId: string } | null }> {
  const deadline = Date.now() + timeoutMs
  while (!isAuthenticated()) {
    if (Date.now() >= deadline) {
      console.warn('[SC-158] No Parse session while loading the background preference; keeping the cached value.')
      return { failed: true, read: null }
    }
    await new Promise((resolve) => setTimeout(resolve, pollMs))
  }

  // SC-452: who this answer belongs to. The store learns who is signed in a few
  // round trips after Parse does, so the caller cannot tell from the store alone.
  const userId = currentUserId()!
  try {
    const media = await playerConfigService.getMediaConfig()
    const effect = parseBackgroundEffect(media?.backgroundEffect)
    return { failed: false, read: effect ? { effect, userId } : null }
  } catch (error) {
    console.warn('[SC-158] Could not load the durable background preference:', error)
    return { failed: true, read: null }
  }
}

/**
 * The newest pick still waiting for its durable write, with the user who made
 * it, and the chain those writes run on.
 *
 * Pickers fire and forget, so two quick picks race. Parse queues saves of the
 * same object in call order, but each save first looks the Profile up, and when
 * the earlier pick's lookup answers last its older effect is what stays stored.
 * Running the writes one at a time, each taking whatever is newest when it
 * starts, makes the last pick win and folds a burst of picks into one write.
 *
 * The user travels with the pick because a queued write runs later, under
 * whatever session exists then: after a sign-out and someone else's sign-in it
 * would put one person's choice on another person's Profile.
 */
let pendingDurablePick: { effect: BackgroundEffect; userId: string } | null = null
let durableWrites: Promise<void> = Promise.resolve()

async function writePendingDurableEffect(): Promise<void> {
  const pick = pendingDurablePick
  // An earlier task in the chain already wrote this pick along with its own.
  if (!pick) return
  pendingDurablePick = null

  if (currentUserId() !== pick.userId) {
    // The cached copy is device-local and the next sign-in reloads the durable
    // one, so dropping this write loses nothing that belonged to the new user.
    console.debug('[SC-158] Session changed before the background preference was saved; not writing it.')
    return
  }
  const { effect } = pick

  try {
    // Only the `media` key, onto what is actually stored — never the whole
    // config from a read that may have quietly returned defaults.
    await playerConfigService.updateMediaConfig({ backgroundEffect: effect })
  } catch (error) {
    // Non-fatal: the cached value already carries the preference on this device.
    console.warn('[SC-158] Could not persist the background preference durably:', error)
    // SC-452: refused because the session has ended (the account signed in
    // elsewhere)? Every later save would fail the same way, with the pick kept
    // on this device only and nobody told. Not awaited: these writes run one at
    // a time, and a sign-out that never answers must not stop the next one.
    // Only while the pick's user is still the one signed in: the save went out
    // under their token, so a 209 that lands after someone else signed in is
    // about the old session, and must not sign the new user out.
    if (currentUserId() === pick.userId) void reportEndedSession(error)
  }
}

/**
 * Persist to both layers. `localStorage` is written first and synchronously, so
 * the preference survives even if the durable write fails or the tab closes
 * before it lands. Resolves once the durable write that carries `effect`, or a
 * newer pick, has finished; it never rejects.
 */
export async function saveBackgroundEffectPreference(effect: BackgroundEffect): Promise<void> {
  const userId = currentUserId()
  writeCachedBackgroundEffect(effect, userId)

  if (!userId) {
    // Signed out or a meeting guest: the pick applies to this page session only,
    // and nothing is remembered on this device or saved to an account (SC-452).
    console.warn('[SC-452] No Parse session; background choice applies to this session only.')
    return
  }

  pendingDurablePick = { effect, userId }
  durableWrites = durableWrites.then(writePendingDurableEffect)
  await durableWrites
}

/**
 * "Turn on camera without background" on a device that cannot run effects.
 *
 * A saved effect follows the user to every device, including ones that cannot
 * apply it, and there the camera stays off rather than show the room the
 * effect was chosen to hide. This is the user's explicit way out **for this
 * device only**: it lives in `localStorage`, never in the cross-device
 * preference, so the effect still applies everywhere it can run.
 *
 * Keyed by user, so a shared machine does not carry one person's choice over
 * to the next person's camera.
 */
const CAMERA_WITHOUT_BACKGROUND_KEY_PREFIX = 'cameraWithoutBackgroundOnThisDevice:'

function currentUserKey(): string {
  try {
    return `${CAMERA_WITHOUT_BACKGROUND_KEY_PREFIX}${Parse.User.current()?.id ?? 'signed-out'}`
  } catch {
    return `${CAMERA_WITHOUT_BACKGROUND_KEY_PREFIX}signed-out`
  }
}

export function readCameraWithoutBackgroundOnThisDevice(): boolean {
  try {
    return localStorage.getItem(currentUserKey()) === 'true'
  } catch {
    return false
  }
}

export function writeCameraWithoutBackgroundOnThisDevice(allowed: boolean): void {
  try {
    if (allowed) localStorage.setItem(currentUserKey(), 'true')
    else localStorage.removeItem(currentUserKey())
  } catch {
    // Blocked storage: the choice still holds for this session through the
    // caller's own state, it just will not be remembered.
  }
}

